import React, { createContext, useContext, useState, useMemo } from 'react';
import { SmartPlayerContextValue } from '../types';

const SmartPlayerContext = createContext<SmartPlayerContextValue | undefined>(
    undefined,
);

interface SmartPlayerProviderProps {
    fps: number;
    children: React.ReactNode;
}

export const SmartPlayerProvider = ({
    fps,
    children,
}: SmartPlayerProviderProps) => {
    const [inPoint, setInPoint] = useState<number | null>(null);
    const [outPoint, setOutPoint] = useState<number | null>(null);

    const value = useMemo<SmartPlayerContextValue>(
        () => ({
            fps,
            inPoint,
            outPoint,
            setInPoint: (time) => {
                if (outPoint !== null && time !== null && time > outPoint) {
                    setOutPoint(null);
                }
                setInPoint(time);
            },
            setOutPoint: (time) => {
                if (inPoint !== null && time !== null && time < inPoint) {
                    setInPoint(null);
                }
                setOutPoint(time);
            },
            clearSubclip: () => {
                setInPoint(null);
                setOutPoint(null);
            },
        }),
        [fps, inPoint, outPoint],
    );

    return (
        <SmartPlayerContext.Provider value={value}>
            {children}
        </SmartPlayerContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSmartPlayerContext = () => {
    const context = useContext(SmartPlayerContext);
    if (!context) {
        throw new Error(
            'SmartPlayer components must be wrapped in a <SmartPlayer> root.',
        );
    }
    return context;
};
